import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { translate } from 'helpers/translateHelper';
import { IconTrendComment } from 'svgIcons/MotionPortalIcons';
import useUserCapability from 'sharedHooks/useUserCapability';
import { isEqual, join } from 'lodash';
import moment from 'moment';
import { formatDate, formatTime } from 'helpers/dateTimeFormatter';
import { apiCallStatus } from 'helpers/constants';
import { splitSelectedSignals } from './helpers';
import { useReportIssue } from 'sharedHooks/useReportIssue';
import TrendCommentsModalContent from './TrendCommentsModalContent';

const PAYLOAD_DATE_FORMAT = 'YYYY-MM-DD';
const PAYLOAD_TIME_FORMAT = 'HH:mm:ss';

const useVisibilityControl = () => {
    const [visible, setVisible] = useState(false);
    return {
        visible,
        setVisibilityOn: () => setVisible(true),
        setVisibilityOff: () => setVisible(false)
    };
};

const isABBEmail = (email) =>
    email?.toLowerCase().includes('.abb.com') || email?.toLowerCase().includes('@abb.com');

const validateFieldChange = (field, value) => {
    if (field === 'subject' && value.length > 100) {
        return { subject: translate('ABB.Powertrain.Frontend.opTrendCommentsModalSubjectError') };
    }
    if (field === 'comment' && value.length > 500) {
        return { comment: translate('ABB.Powertrain.Frontend.opTrendCommentsModalCommentError') };
    }
    return {};
};

const buildFormErrors = (formData, selectedTrendKPIs) => {
    const newErrors = {};
    if (!formData.date.trim()) {
        newErrors.date = translate('ABB.Powertrain.Frontend.opTrendCommentsModalErrorMsg');
    }
    if (!formData.time.trim()) {
        newErrors.time = translate('ABB.Powertrain.Frontend.opTrendCommentsModalErrorMsg');
    }
    if (!formData.isAllSignals && selectedTrendKPIs?.length && formData.selectedSignals.length === 0) {
        newErrors.selectedSignals = translate('ABB.Powertrain.Frontend.opTrendCommentsModalErrorMsg');
    }
    if (!formData.subject.trim()) {
        newErrors.subject = translate('ABB.Powertrain.Frontend.opTrendCommentsModalErrorMsg');
    }
    if (!formData.comment.trim()) {
        newErrors.comment = translate('ABB.Powertrain.Frontend.opTrendCommentsModalErrorMsg');
    }
    return newErrors;
};

const applyErrorUpdate = (field, newErrors, errors, setErrors) => {
    if (Object.keys(newErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
    }
};

const clearAllSignalsSelection = (errors, setFormData, setErrors) => {
    setFormData(prev => ({ ...prev, selectedSignals: [] }));
    if (errors.selectedSignals) {
        setErrors(prev => ({ ...prev, selectedSignals: '' }));
    }
};

const isAcceptButtonDisabled = (errors, loadingStatus, dateValid) =>
    Object.keys(errors).some(error => errors[error]) ||
    loadingStatus === apiCallStatus.LOADING ||
    !dateValid;

const shouldShowError = (hideError, loadingStatus, errorData) =>
    !hideError &&
    loadingStatus === apiCallStatus.ERROR &&
    Boolean(errorData?.error);

const getFormattedCommentDateTime = (timestamp, type) => {
    if (type === 'date') {
        return formatDate(timestamp, { dateFormat: PAYLOAD_DATE_FORMAT });
    }
    if (type === 'time') {
        return formatTime(timestamp, { timeFormat: PAYLOAD_TIME_FORMAT });
    }
};

const TrendCommentsModal = ({
    isShowModal,
    setIsShowModal,
    trendCommentsCreateEditLoadingStatus,
    trendCommentsCreateEditErrorData,
    createAndEditTrendCommentRequest,
    clearCreateEditTrendCommentError,
    editCommentData,
    selectedAsset,
    selectedTrendKPIs,
    trendKPIsList,
    graphCommentTimestamp,
    userEmail
}) => {

    const userCapabilityHelper = useUserCapability();
    const { showReportIssue } = useReportIssue();
    const isREC = userCapabilityHelper.hasHomePageRecCapability(); /* FALSE FOR LSU AND END USER | TRUE FOR REC USER */
    const isLSU = userCapabilityHelper.hasFleetOverviewCapability() || userCapabilityHelper.hasHomePageCapability();
    const isABBUser = userEmail && isABBEmail(userEmail);
    const datePickerVisibility = useVisibilityControl();

    const getCommentDate = () => {
        if (editCommentData?.timestamp) {
            return getFormattedCommentDateTime(editCommentData.timestamp, 'date');
        }
        if (graphCommentTimestamp) {
            return getFormattedCommentDateTime(graphCommentTimestamp, 'date');
        }
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getCommentTime = () => {
        if (editCommentData?.timestamp) {
            return getFormattedCommentDateTime(editCommentData.timestamp, 'time');
        }
        if (graphCommentTimestamp) {
            return getFormattedCommentDateTime(graphCommentTimestamp, 'time');
        }
        const now = new Date();
        return now.toTimeString().split(' ')[0];
    };

    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getCurrentTime = () => {
        const now = new Date();
        return now.toTimeString().split(' ')[0];
    };

    const [formData, setFormData] = useState({
        date: getCommentDate(),
        time: getCommentTime(),
        isAllSignals: isEqual(selectedTrendKPIs, splitSelectedSignals(editCommentData?.timeseriesKey)),
        selectedSignals: trendKPIsList?.filter(kpi => splitSelectedSignals(editCommentData?.timeseriesKey)?.some(selectedKpi => selectedKpi === kpi.timeseriesKey)) || [],
        subject: editCommentData?.subjectText || '',
        comment: editCommentData?.commentText || '',
        isPublic: editCommentData?.internal || false
    });

    const [errors, setErrors] = useState({
        date: '',
        time: '',
        selectedSignals: '',
        subject: '',
        comment: ''
    });

    const [hideCreateEditErrorView, setHideCreateEditErrorView] = useState(false);

    const showSignalSelector = !formData.isAllSignals && selectedTrendKPIs?.length > 0;
    const showVisibilityToggle = (isREC || isLSU) && isABBUser && (!editCommentData?.id || editCommentData?.isInternalAuthor);

    useEffect(() => {
        if (isShowModal) {
            setHideCreateEditErrorView(false);
        }
    }, [isShowModal]);

    // Generic onChange function to update state object
    const handleChange = (field, value) => {
        const newErrors = validateFieldChange(field, value);

        // Block future time selection when today's date is selected
        if (field === 'time' && formData.date === getCurrentDate() && value > getCurrentTime()) {
            return; // Don't update if future time
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        applyErrorUpdate(field, newErrors, errors, setErrors);

        if (field === 'isAllSignals' && value) {
            clearAllSignalsSelection(errors, setFormData, setErrors);
        }
    };

    // Form validation function
    const validateForm = () => {
        const newErrors = buildFormErrors(formData, selectedTrendKPIs);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getUTCTime = () => {
        const localDateTime = new Date(`${formData.date}T${formData.time}`);
        return moment.utc(localDateTime);
    };

    const joinSelectedSignalss = (signals) =>
        formData.isAllSignals
            ? join(signals, ', ')
            : join(signals.map(signal => signal.timeseriesKey), ', ');

    const getMinDate = () => {
        return moment().utc().startOf('day').subtract(364, 'days').format('YYYY-MM-DD');
    };

    const isDateValid = () => {
        const selected = moment.utc(formData.date, 'YYYY-MM-DD').startOf('day');
        const minAllowed = moment().utc().startOf('day').subtract(364, 'days');
        return selected.isSameOrAfter(minAllowed, 'day') && selected.isSameOrBefore(moment().utc().startOf('day'), 'day');
    };

    // Handle form submission
    const handleAccept = () => {
        setHideCreateEditErrorView(false);
        if (validateForm()) {
            const payload = {
                id: editCommentData?.id || 0,
                assetId: selectedAsset?.id,
                timestamp: getUTCTime().toISOString(),
                timeseriesKey: formData?.isAllSignals ? joinSelectedSignalss(selectedTrendKPIs) : joinSelectedSignalss(formData.selectedSignals),
                subjectText: formData?.subject?.trim(),
                commentText: formData?.comment?.trim(),
                internal: formData?.isPublic
            };
            createAndEditTrendCommentRequest(payload);
        }
    };

    const handleRetry = () => {
        setHideCreateEditErrorView(true);
        clearCreateEditTrendCommentError();
    };

    const showCreateEditError = shouldShowError(
        hideCreateEditErrorView,
        trendCommentsCreateEditLoadingStatus,
        trendCommentsCreateEditErrorData
    );

    return (
        <GeneralDialog
            show={isShowModal}
            close={() => setIsShowModal(false)}
            title={
                <div className='trend-comments-modal-title'>
                    <IconTrendComment />
                    {editCommentData?.id ? translate('ABB.Powertrain.Frontend.opTrendCommentsEditModalTitle') : translate('ABB.Powertrain.Frontend.opTrendCommentsModalTitle')}
                </div>
            }
            modalClassName='trend-comments-dialog'
            backdropClassName='trend-comments-backdrop'
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
            }}
            acceptButtonProps={{
                disabled: isAcceptButtonDisabled(errors, trendCommentsCreateEditLoadingStatus, isDateValid()),
                text: editCommentData?.id ? translate('ABB.Powertrain.Frontend.saveButton') : translate('ABB.Powertrain.Frontend.opTrendCommentsModalAcceptBtn'),
                onClick: handleAccept
            }}
            closeButton={true}
        >
            <TrendCommentsModalContent
                loadingStatus={trendCommentsCreateEditLoadingStatus}
                showError={showCreateEditError}
                errorData={trendCommentsCreateEditErrorData}
                onRetry={handleRetry}
                onReportIssue={showReportIssue}
                formProps={{
                    formData,
                    errors,
                    handleChange,
                    datePickerVisibility,
                    minDate: getMinDate(),
                    maxDate: getCurrentDate(),
                    graphCommentTimestamp,
                    selectedTrendKPIs,
                    trendKPIsList,
                    showSignalSelector,
                    showVisibilityToggle
                }}
            />
        </GeneralDialog>
    );
};

TrendCommentsModal.propTypes = {
    isShowModal: PropTypes.bool.isRequired,
    setIsShowModal: PropTypes.func.isRequired,
    trendCommentsCreateEditLoadingStatus: PropTypes.string,
    trendCommentsCreateEditErrorData: PropTypes.shape({
        loadingStatus: PropTypes.string,
        errorCode: PropTypes.number,
        error: PropTypes.object
    }),
    createAndEditTrendCommentRequest: PropTypes.func.isRequired,
    clearCreateEditTrendCommentError: PropTypes.func.isRequired,
    editCommentData: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        timestamp: PropTypes.string,
        timeseriesKey: PropTypes.string,
        subjectText: PropTypes.string,
        commentText: PropTypes.string,
        internal: PropTypes.bool,
        isInternalAuthor: PropTypes.bool
    }),
    selectedAsset: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    selectedTrendKPIs: PropTypes.arrayOf(PropTypes.string),
    trendKPIsList: PropTypes.arrayOf(PropTypes.shape({
        timeseriesKey: PropTypes.string.isRequired,
        timeseriesName: PropTypes.string.isRequired
    })),
    graphCommentTimestamp: PropTypes.string,
    userEmail: PropTypes.string
};

export default TrendCommentsModal;
